// libraries
import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { get, startCase, lowerCase, sortBy, get as _get } from 'lodash';

// store and utils
import * as actions from '../../../store/actions';
import * as ODCalls from '../../../store/calls/indicativeOptionData';
import validators from '../../../utils/forms/validators';
import formatters from '../../../utils/forms/formatters';
import api from '../../../store/api';
import { isUserLoggedIn } from '../../../utils/session/logout';

// components
import FormFlowWrapper from '../../theme/wrappers/FormFlowWrapper';
import FormFlowStepForm from '../../theme/forms/FormFlowStepForm';
import BaseForm from '../../theme/forms/BaseForm';
import Input from '../../theme/forms/Input';
import ChevronSteps from '../../theme/forms/ChevronSteps';
import {
	addErrorAlert,
	toggleGlobalLoader,
} from '../../../store/calls/alertLoadingDispatch';
import SelectByCategory from '../../theme/forms/SelectByCategory';
import InfoFeed from '../../theme/cardlists/infoFeed';
import FormFlow from '../../theme/formFlows/FormFlow';
import TabsSteps from '../../theme/forms/TabsSteps';
import { updateOrCreateProjectLead } from '../../../store/calls/indicativeOptionData';
import StaticCornerMap from '../../theme/maps/staticCornerMap';
import AlertCard from '../../theme/cards/AlertCard';
import { getSubdomain } from '../../../utils/general';
import TelephoneNumber from '../../subcomponents/TelephoneNumber';
import ContactEmail from '../../subcomponents/ContactEmail';
import ConfirmModal from "../../theme/modals/ConfirmModal";
import LoginModal from "../../subcomponents/auth/LoginModal";
import StaticMapboxImage from "../../theme/maps/staticMap";
import TaxInputForm from "../../subcomponents/admin/TaxInputForm";


class IndicativeFormFlow extends React.Component {
	constructor(props) {
		super(props);

		this.state = {
			inputs: [],
			errors: {},
			currTab: 4,
			multiScopeConfirmed: false,
			showMultiScopeConfirmModal: false,
			showLoginModal: false
		};
	}

	componentDidMount() {
		toggleGlobalLoader(true);
		Promise.allSettled([
			ODCalls.getAssetTypes(),
			ODCalls.getBusinessTypes(),
			ODCalls.getAvailableImprovements(),
		])
			.catch(() => {
				addErrorAlert('Failed to load all initial data.', 10 * 1000);
			})
			.finally(() => {
				toggleGlobalLoader(false);
			});

		// for debugging particular steps
		// this.props.setCurrStep(5);
	}

	setCurrentTab = (tab) => {
		this.setState({ ...this.state, currTab: tab });
	};

	getBusinessTypes = () => {
		api
			.get('/business-types/')
			.then((res) => this.setState({ businessTypes: res.data }));
	};

	getAssetClasses = () => {
		api
			.get('/asset-types/')
			.then((res) => this.setState({ assetTypes: res.data }));
	};

	getFormFlowConfig() {
		return {
			title: 'Get Indicative',
			onComplete: this.onFormFlowComplete,
			steps: [
				// step 1 what improvements are you making?
				{
					title: 'What improvements are you making?',
					onComplete: () => {
						if (!this.props.focusedProject) {
							this.createProjectInDB();
						} else {
							this.updateProjectInDB();
						}
					},
					component: this.renderSelectImprovementStep(
						'What improvement(s) are you making?'
					),
					areStepReqsMet: () =>
						!!get(this.props, 'inputs.improvements', []).length,
				},
				// step 2 set value to improvements
				{
					title: 'How much capital do you need for these improvements?',
					onComplete: () => {
						this.updateProjectInDB();
					},
					inputs: this.getImprovementValueInputs(),
					areStepReqsMet: () =>
						!!get(this.props, 'inputs.improvements', []).every(
							(d) => d.value && !isNaN(d.value)
						),
				},
				{
					// step 3
					title: 'What kind of asset is this?',
					onComplete: () => {
						this.updateProjectInDB();
					},
					inputs: [
						{
							type: 'select',
							dataKey: 'business_type',
							label: 'Business Type',
							inputClasses: 'is-medium is-fullwidth',
							controlClasses: 'is-expanded',
							placeholder: '--',
							options: this.props.optionData.businessTypes.map((d) => ({
								value: +d.id,
								display: d.name,
							})),
							...validators.exists,
						},
						{
							type: 'select',
							dataKey: 'asset_type',
							label: 'Asset Type',
							inputClasses: 'is-medium is-fullwidth',
							controlClasses: 'is-expanded',
							placeholder: '--',
							options: sortBy(
								this.props.optionData.assetTypes.map((d) => ({
									value: d.id,
									display: d.name,
								})),
								'display'
							),
							...validators.exists,
						},
						{
							type: 'select',
							dataKey: 'ownership_status',
							label: 'Is the property owned or leased?',
							inputClasses: 'is-medium is-fullwidth',
							controlClasses: 'is-expanded',
							placeholder: '--',
							options: [
								{
									value: 'own',
									display: 'Owned',
								},
								{
									value: 'lease',
									display: 'Leased',
								},
							],
							...validators.exists,
						},
					],
				},
				// step 4 property data
				{
					title: 'Some information about your project',
					onComplete: () => {
						this.updateProjectInDB();
					},
					// title: "Tell us a little more about your project",
					inputs: [...this.getStep4Inputs()].map((d) => ({
						...this.updateInputWithDataKey(d.dataKey),
						...d,
					})),
				},
				// step 5 -- name your project
				{
					title: 'Give your project a name',
					onComplete: () => {
						this.updateProjectInDB();
					},
					inputs: [
						{
							type: 'text',
							dataKey: 'project_name',
							label: 'Project Name',
							inputClasses: 'is-medium',
							...validators.isStringOfMinLength(2),
						},
					],
				},
				// step 6, rejection, acceptance, or prop tax help question -- we move past this at the end
				{
					title: 'This project has tax benefits.',
					onComplete: () => {
						this.updateProjectInDB();
					},
					component: (
						<TaxInputForm
							onNoTax={this.onFormFlowComplete}
						/>
					),
				},
			],
		};
	}

	// returns speadable dict to update the value of inputs in the form flow
	updateInputWithDataKey = (dataKey) => {
		return {
			onChange: (val) => this.props.updateInputs({ [dataKey]: val }),
		};
	};

	updateProjectInDB = (extraData) => {
		let data = {
			data: {
				...this.props.computed,
				...this.props.inputs,
				id: this.props.focusedProject,
                ...extraData
			},
		};

		return updateOrCreateProjectLead(data);
	};

	createProjectInDB = () => {
		const subdomain = getSubdomain();
		let data = {
			user: this.props.userData.user_id,
			pace_available: true,
			subdomain,
			data: {
				...this.props.computed,
				...this.props.inputs,
				id: this.props.focusedProject,
				subdomain: getSubdomain(),
			},
		};

		return updateOrCreateProjectLead(data).then((res) => {
			this.props.setFocusedProject(res.data.id);
			return res;
		});
	};

	// select improvements

	onSelectImprovement = (selected, id) => {
		let improvements = get(this.props.inputs, 'improvements', []);
		if (selected)
			improvements = [...improvements.filter((d) => d.id !== +id), { id }];
		if (!selected) improvements = improvements.filter((d) => d.id !== id);
		this.props.updateInputs({ improvements });
	};

	renderSelectImprovementStep(title) {
		const multipleCategoriesSelected =
			[
				...new Set(
					(this.props.inputs?.improvements || [])
						.map((d) =>
							(this.props.optionData.improvements || []).find(
								(imp) => imp.id === d.id
							)
						)
						.filter((d) => d)
						.map((d) => d.category)
				),
			].length > 1;

		let improvementsData = sortBy(
			this.props.optionData.improvements || [],
			'category'
		);
		return (
			<>
				{/* <div className={"form-section-header"}>{title}</div> */}
				{/* this horrendous piece of code asks: is there more than one category selected? */}
				{multipleCategoriesSelected && (
					<div className="mb-4">
						<AlertCard
							type="warning"
							msg={`If you have a multi-scope project, you will receive the greatest number of 
                  funding proposals by separating your scopes into different projects.`}
						/>
					</div>
				)}
				<SelectByCategory
					data={improvementsData.map((d) =>
						get(this.props.inputs, 'improvements', [])
							.map((imp) => imp.id)
							.includes(d.id)
							? { ...d, selected: true }
							: d
					)}
					onOptionValueChange={this.onSelectImprovement}
					topLevelLeftHTML={
						<div className="level-item">
							<div
								className="button is-primary"
								onClick={() => {
									this.onSelectImprovement(true, 32);
									if (!this.props.focusedProject) {
										this.createProjectInDB();
									} else {
										this.updateProjectInDB();
									}
									this.props.setCurrStep(this.props.currStep + 1);
									this.sendGtagNextStepEvent();
								}}
							>
								<i className="fas fa-sun mr-3" />
								I'm only looking for solar!
							</div>
						</div>
					}
					startExpanded={false}
				/>

				{multipleCategoriesSelected && (
					<div className="mt-4">
						<AlertCard
							type="warning"
							msg={`If you have a multi-scope project, you will receive the greatest number of 
                  funding proposals by separating your scopes into different projects.`}
						/>
					</div>
				)}
			</>
		);
	}

	// price the value of each improvement
	getImprovementValueInputs() {
		let improvements = get(this.props, 'inputs.improvements', []);
		let inputs = improvements.map((d) => {
			return {
				type: 'text',
				dataKey: `improvement_val_${d.id}`,
				label: `Estimated value of ${
					(
						this.props.optionData.improvements.find((imp) => imp.id === d.id) ||
						{}
					).name
				}`,
				inputClasses: 'is-medium',
				...validators.isNumberNotNull,
				...formatters.USDCurrency,
				onChange: (val) => {
					let idx = improvements.findIndex((imp) => imp.id === d.id);
					val = val.replace(/[^0-9.]/g, '')
					improvements[idx].value = val;
					this.props.updateInputs({ improvements });
				},
				value: (improvements.find((imp) => imp.id === d.id) || {}).value,
			};
		});

		return inputs;
	}

	getStep4Inputs = () => {
		let improvements = _get(this.props, 'inputs.improvements', []).map(
			(d) => d.id
		);
		let project_includes_solar = improvements.includes(32); // improvement includes solar

		let inputs = [
			{
				type: 'date',
				dataKey: 'start_date',
				label: 'Approx Project Start Date',
				subLabel: 'Used to calculate capitalized interest',
				inputClasses: 'is-medium',
				validator: (val) => val, // && !lowerCase(formatters.date("Ldyy", "LLLL d, yyyy").formatter(val)).includes('invalid'),
				// ...formatters.date("Ldyy", "LLLL d, yyyy"),
			},
		];

		if (project_includes_solar) {
			inputs.push({
				type: 'text',
				dataKey: 'solar_system_size',
				label: 'How large is the solar system in kW DC?',
				subLabel:
					"<b>You can leave this blank if you aren't sure, but some financing products may not be quoted.</b>",
				validator: (val) => [null, undefined, ''].includes(val) || (!isNaN(parseInt(val)) && parseInt(val) <= 5000 && parseInt(val) >= 10),
				...formatters.number,
				inputClasses: 'is-medium',
				errorText: 'The system size must be a number greater than 10 and less than 5,000. For larger projects than that, please call us directly.',
				// defaultValue: '.5'
			});

			/*

			REMOVED BECAUSE ALL PPAS ARE NOW BIDDESK


			inputs.push({
				type: 'text',
				dataKey: 'solar_system_annual_production',
				label:
					'How much energy do you anticipate your solar system will produce in its first year? (in kWh)',
				subLabel:
					'<b>You can leave this blank, but PPA (Power Purchase Agreement) financing will not be quoted.</b>',
				inputClasses: 'is-medium',
				...validators.isNumber.validator(),
				...formatters.number.formatter(),
				validator: (val) => [null, undefined, ''].includes(val) || (!isNaN(parseInt(val)) && parseInt(val) >= 10000),
				...formatters.number,
				errorText: 'The production must be larger than 10,000 kWh',
				onChange: (val) => {
					if (val && typeof val === 'string') {
						val = val.replace(/\D/g, '');
					}
					this.props.updateInputs({ solar_system_annual_production: val });
				},
			});

			*/
		}

		return inputs;
	};



	splitScopes = () => {
		/*
		 * This function examines the scopes of the different projects to ensure that they can be included in one project
		 * Improvements with tax benefits each have their own scope. Improvements without tax benefits are lumped into the same project.
		 *
		 * @returns: an array of arrays, containing the selected improvements, assigned by project
		 * */

		let {
			optionData: { improvements: improvementsData },
			inputs: { improvements: inputImprovements },
		} = this.props;

		// score separated scoped into different projects;
		// no tax improvements, or only one project, goes to zero index.
		// every scope with an improvement goes to a new index;
		let scopes = [[], []];
		inputImprovements = (inputImprovements || []).filter(d => d.value);
		inputImprovements.forEach((d) => {
			let imp = {
				...(improvementsData.find((_d) => _d.id === d.id) || {}),
				...d,
			};
			if (!imp.tax_benefit_fixed_subsidy && !imp.tax_benefit_percentage && !imp.direct_pay_percentage) {
				scopes[0].push(imp);
			} else {
				scopes[1].push([imp]);
			}
		});

		// we need to handle the case of their only being a scope with a tax benefit, which would leave the first
		// array empty in this circumstance. So last step is to delete empty scopes
		scopes = scopes.filter(d => d.length)

		return scopes;

	}


	// create several projects for each scope,
	doFinalUpdateOnSplitScopes = () => {

		const scopes = this.splitScopes();

		// is projects length is only one, proceed as normal
		if (scopes.length === 1) {
			return this.updateProjectInDB().then(res => [res.data]);
		} 
			let promises = [];
			scopes.forEach((p, i) => {
				let data = {
					data: {
						...this.props.computed,
						...this.props.inputs,
						id: i === 0 ? this.props.focusedProject : null,
						improvements: p,
						project_name: `${this.props.inputs?.project_name} (${p.map(
							(imp, _i) => `${imp.name}${_i !== p.length - 1 ? ', ' : ''}`
						)})`,
					},
					user: this.props.userData.user_id,
				};

				promises.push(updateOrCreateProjectLead(data).then(res => res.data));
			});

			return Promise.all(promises);
		
	};

	onFormFlowComplete = () => {
		/*
		 * If the user is logged, pretty simple -- just dump them over to the indicative page and give them their loan estimates
		 * If the user isn't logged in, we need them to create an account before we show them the goods.
		 * */
		let splitScopeRequired = this.splitScopes().length > 1;
		if (splitScopeRequired && !this.state.multiScopeConfirmed) {
			this.setState({ showMultiScopeConfirmModal: true})
			return;
		}

		let userIsLoggedIn = isUserLoggedIn();

		this.doFinalUpdateOnSplitScopes()
			.then((res) => {
				console.log("RES RES RES", res )
				// returns array of projects. First project goes to main tab.
				// Other projects go to other tabs
				/* TODO... WE HAVE TO HANDLE AUTH REDIRECTION */

				let pushQueue = [...res];
				let tabsToOpen = []
				while (pushQueue.length > 1) {
					let projToOpen = pushQueue.pop();
					let url = `/project-estimate/${projToOpen.id}`;
					tabsToOpen.push({url, tabName: projToOpen.id});
				}

				let currentTabRedirectPath = '/project-estimate/' + pushQueue[0].id

				if (userIsLoggedIn) {
					tabsToOpen.forEach(d => window.open(d.url, d.tabName))
					this.props.history.push(currentTabRedirectPath);
				} else {
					this.props.setSessionVar({tabsToOpenOnCompletion: tabsToOpen});
					this.props.setSessionVar({onLoginPath: currentTabRedirectPath});
					this.setState({showLoginModal: true})
				}
			});

		// if (user_logged_in) {
		//   new Promise(resolve => {
		//     this.updateProjectInDB();
		//     resolve();
		//   }).then(() => {
		//     this.props.history.push('/project-estimate/' + this.props.focusedProject);
		//   })
		// } else {
		//   this.props.setSessionVar('user_creating_project', this.props.focusedProject)
		//   window.gtag('event', 'formflow_complete', {page: window.location.pathname})
		//   this.props.history.push('/create-account/')
		// }
	};

	renderMultiScopeConfirmModal = () => {

		let scopes = this.splitScopes();

		let scopedDisplay = scopes.map((d,i) => (
			<p key={i}>Scope {i + 1}: {d.map((imp, _i) => `${imp.name}${_i !== d.length - 1 ? ', ' : ''}`)}</p>
		))

		return (
			<ConfirmModal
				visible={this.state.showMultiScopeConfirmModal}
				onAccept={() => {
					this.setState({
						multiScopeConfirmed: true,
						showMultiScopeConfirmModal: false
					}, this.onFormFlowComplete)
				}}
				onCancel={() => this.setState({ showMultiScopeConfirmModal: false })}
				prompt="This project must be split into multiple scopes."
			>
				<div>
					{scopedDisplay}
					<p className="mt-4 mb-6 has-text-weight-semibold">
						Additional scopes will open in new tabs. Please allow popups
						in your browser to view all your project options.
					</p>
				</div>
			</ConfirmModal>
		)
	}



	renderStepsAndTitle() {
		return (
			<>
				{/** DESKTOP */}
				<h2 className="is-size-2 has-text-primary mb-4 only-desktop">
					Get Your Commercial Financing Options
				</h2>
				<ChevronSteps stepCount={5} activeStep={this.props.currStep} />
				{/** MOBILE */}
				{/* <div className="tabs tabs-heading only-mobile"> */}
				{/*  <ul> */}
				{/*    <li className={this.state.currTab == 1 ? "is-active" : ""}> */}
				{/*      <a onClick={() => this.setCurrentTab(1)}>Get an Indicative</a> */}
				{/*    </li> */}
				{/*    <li className={this.state.currTab == 2 ? "is-active" : ""}> */}
				{/*      <a onClick={() => this.setCurrentTab(2)}>Your Project</a> */}
				{/*    </li> */}
				{/*  </ul> */}
				{/* </div> */}
			</>
		);
	}

	renderSideCards() {
		return (
			<div className="info-feed is-hidden-touch">
				<div className="card mb-3">
					<div className="card-content">
						<div className="card-header-title is-size-5 has-text-primary pl-0">
							Project Address
						</div>
						<p className="my-2">{this.props.computed.address?.formatted_address}</p>
						<StaticMapboxImage latitude={this.props.computed.address?.lat} longitude={this.props.computed.address?.lng} />
						<div className="my-2 button is-text pl-0" onClick={() => this.props.history.push('/')}>Look up another address</div>
					</div>
				</div>
				<div className="card mb-3">
					<div className="card-content">
						<div className="card-header-title is-size-5 has-text-primary pl-0">
							Got Questions ? Need help?
						</div>
						<p>
						<TelephoneNumber className="is-size-3 has-text-semibold" />
						</p>
						<ContactEmail
							className="is-size-6 has-text-black-bis has-text-semibold"
						/>
					</div>
				</div>
				{!!(this.props.computed?.programs || []).length && (
					<>
						{/* <StaticCornerMap lat={lat} lng={lng} style={{opacity: .7}}/> */}
						<div className="box">
							<div className="has-text-success is-flex">
								<span className="icon mt-1 mr-5 is-size-3">
									<i className="fas fa-check-circle" />
								</span>
								<span className="is-size-5 has-text-weight-semibold">
									This property is pace eligible!
								</span>
							</div>
						</div>
					</>
				)}
			</div>
		);
	}

	renderMobileFooter() {
		return (
			<footer className="footer is-hidden-desktop">
				<div className="content has-text-centered">
					<p>
						<strong>Have Questions? Call now. </strong>
					</p>
					<p className="is-size-3">
						<TelephoneNumber />
					</p>
					<p>
						<a href="mailto:admin@cleanfi.com">admin@cleanfi.com</a>
					</p>
				</div>
			</footer>
		);
	}



	render() {
		let { lat, lng } = _get(this.props.computed, 'address', {
			lat: 37.819929,
			lng: -122.478255,
		}); // defaults to golden gate bridge, because why not. Needed for static corner map

		return (
			<FormFlowWrapper>
				{this.renderStepsAndTitle()}
				<div className="columns">
					<div className="column is-three-fifths">
						<FormFlow {...this.getFormFlowConfig()} />
					</div>

					<div className="column">{this.renderSideCards()}</div>
				</div>
				{this.renderMobileFooter()}
				{this.renderMultiScopeConfirmModal()}
				<LoginModal
					visible={this.state.showLoginModal}
					// onClose={() => this.setState({ showLoginModal: false })}
					onLogin={(userData) => {
						api.patch(`/project-leads/${this.props.focusedProject}/`, {user: userData.user_id})
					}}
				/>
			</FormFlowWrapper>
		);
	}
}

const mapStateToProps = (state) => ({
	inputs: state.formFlow.inputs,
	computed: state.formFlow.computed,
	optionData: state.indicativeOptionData,
	currStep: state.formFlow.currStep,
	infoFeedDisplay: state.infoFeed,
	focusedProject: state.projectLeads.focusedProject,
	userData: state.user.decodedTokenData,
});

const mapDispatchToProps = (dispatch) => ({
	updateInfoFeedDisplay: (data) =>
		dispatch(actions.infoFeed.updateInfoFeedDisplay(data)),
	updateInputs: (data) => dispatch(actions.formFlow.updateFormFlowInputs(data)),
	setFocusedProject: (data) =>
		dispatch(actions.projectLeads.setFocusedProject(data)),
	setCurrStep: (data) => dispatch(actions.formFlow.setFormFlowCurrStep(data)),
	setSessionVar: (data = {}) => dispatch(actions.session.setVar(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(IndicativeFormFlow);
